import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { BarChart3, TrendingUp, Package, Calendar, AlertTriangle, Download, Filter, RefreshCw } from 'lucide-react';
import AdminNavigation from '../../components/ui/AdminNavigation';
import { barrelService } from '../../services/barrelService';
import InventoryTurnoverChart from './components/InventoryTurnoverChart';
import DemandForecastingChart from './components/DemandForecastingChart';
import GeographicAnalysisPanel from './components/GeographicAnalysisPanel';
import PerformanceKPICards from './components/PerformanceKPICards';

export default function BarrelInventoryAnalyticsCenter() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  
  // Analytics data
  const [statistics, setStatistics] = useState(null);
  const [barrelOrders, setBarrelOrders] = useState([]);
  const [barrelDeposits, setBarrelDeposits] = useState([]);
  const [analyticsData, setAnalyticsData] = useState(null);
  
  // Filter states
  const [timeRange, setTimeRange] = useState('30days'); // 7days, 30days, 90days, 1year
  const [barrelTypeFilter, setBarrelTypeFilter] = useState('all'); // all, noir_270l, bleu_220l
  const [regionFilter, setRegionFilter] = useState('all');
  
  // Advanced analytics states
  const [turnoverData, setTurnoverData] = useState([]);
  const [demandForecast, setDemandForecast] = useState([]);
  const [geographicAnalysis, setGeographicAnalysis] = useState([]);
  const [alerts, setAlerts] = useState([]);

  useEffect(() => {
    loadAnalyticsData();
  }, [timeRange, barrelTypeFilter, regionFilter]);

  const loadAnalyticsData = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      else setRefreshing(true);
      setError('');
      
      const [statsData, ordersData, depositsData] = await Promise.all([
        barrelService?.getBarrelStatistics(),
        barrelService?.getBarrelOrders(),
        barrelService?.getBarrelDeposits()
      ]);
      
      setStatistics(statsData);
      setBarrelOrders(ordersData);
      setBarrelDeposits(depositsData);
      
      // Calculate advanced analytics
      const analytics = calculateAdvancedAnalytics(ordersData, depositsData, statsData);
      setAnalyticsData(analytics);
      
      // Calculate turnover data
      const turnover = calculateTurnoverData(ordersData, depositsData);
      setTurnoverData(turnover);
      
      // Generate demand forecast
      const forecast = generateDemandForecast(ordersData);
      setDemandForecast(forecast);
      
      // Analyze geographic performance
      const geoAnalysis = analyzeGeographicPerformance(ordersData, depositsData);
      setGeographicAnalysis(geoAnalysis);
      
      // Generate predictive alerts
      const predictiveAlerts = generatePredictiveAlerts(analytics, forecast);
      setAlerts(predictiveAlerts);
      
    } catch (err) {
      console.error('Error loading analytics data:', err);
      setError(err?.message || 'Erreur lors du chargement des analyses');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const calculateAdvancedAnalytics = (orders, deposits, stats) => {
    const now = new Date();
    const filterDate = getFilterDate(timeRange);
    
    // Filter data by time range
    const filteredOrders = orders?.filter(o => new Date(o?.created_at) >= filterDate);
    const filteredDeposits = deposits?.filter(d => new Date(d?.created_at) >= filterDate);
    
    // Calculate total stock value
    const noirStock = stats?.noir_270l_stock || 0;
    const bleuStock = stats?.bleu_220l_stock || 0;
    const totalStockValue = (noirStock * 35) + (bleuStock * 40);
    
    // Calculate monthly consumption rates
    const monthlyConsumption = {
      noir_270l: calculateMonthlyRate(filteredOrders, 'noir_270l'),
      bleu_220l: calculateMonthlyRate(filteredOrders, 'bleu_220l')
    };
    
    // Calculate average days of supply
    const daysOfSupply = {
      noir_270l: monthlyConsumption?.noir_270l > 0 ? (noirStock / (monthlyConsumption?.noir_270l / 30)) : 999,
      bleu_220l: monthlyConsumption?.bleu_220l > 0 ? (bleuStock / (monthlyConsumption?.bleu_220l / 30)) : 999
    };
    
    // Calculate cost per unit trends
    const avgOrderValue = filteredOrders?.reduce((sum, o) => sum + (o?.total_price || 0), 0) / (filteredOrders?.length || 1);
    
    return {
      totalStockValue,
      monthlyConsumption,
      daysOfSupply,
      avgOrderValue,
      totalOrders: filteredOrders?.length,
      totalDeposits: filteredDeposits?.length,
      deliveryRate: calculateDeliveryRate(filteredOrders),
      collectionRate: calculateCollectionRate(filteredDeposits)
    };
  };

  const calculateMonthlyRate = (orders, barrelType) => {
    const filtered = barrelType === 'all' ? orders : orders?.filter(o => o?.barrel_type === barrelType);
    const totalQuantity = filtered?.reduce((sum, o) => sum + (o?.quantity || 0), 0);
    const daysInRange = getDaysInRange(timeRange);
    return (totalQuantity / daysInRange) * 30;
  };

  const calculateDeliveryRate = (orders) => {
    const delivered = orders?.filter(o => o?.status === 'delivered')?.length || 0;
    return orders?.length > 0 ? (delivered / orders?.length) * 100 : 0;
  };

  const calculateCollectionRate = (deposits) => {
    const collected = deposits?.filter(d => d?.collection_status === 'collected')?.length || 0;
    return deposits?.length > 0 ? (collected / deposits?.length) * 100 : 0;
  };

  const calculateTurnoverData = (orders, deposits) => {
    const months = getMonthsInRange(timeRange);
    
    return months?.map(month => {
      const monthOrders = orders?.filter(o => {
        const orderDate = new Date(o?.created_at);
        return orderDate?.getMonth() === month?.month && orderDate?.getFullYear() === month?.year;
      });
      
      const monthDeposits = deposits?.filter(d => {
        const depositDate = new Date(d?.created_at);
        return depositDate?.getMonth() === month?.month && depositDate?.getFullYear() === month?.year;
      });
      
      return {
        month: month?.label,
        orders: monthOrders?.length,
        deposits: monthDeposits?.length,
        revenue: monthOrders?.reduce((sum, o) => sum + (o?.total_price || 0), 0),
        noir_270l: monthOrders?.filter(o => o?.barrel_type === 'noir_270l')?.reduce((sum, o) => sum + (o?.quantity || 0), 0),
        bleu_220l: monthOrders?.filter(o => o?.barrel_type === 'bleu_220l')?.reduce((sum, o) => sum + (o?.quantity || 0), 0)
      };
    });
  };

  const generateDemandForecast = (orders) => {
    // Simple moving average forecast
    const months = getMonthsInRange('90days');
    const historicalData = months?.map(month => {
      const monthOrders = orders?.filter(o => {
        const orderDate = new Date(o?.created_at);
        return orderDate?.getMonth() === month?.month && orderDate?.getFullYear() === month?.year;
      });
      return monthOrders?.reduce((sum, o) => sum + (o?.quantity || 0), 0);
    });
    
    // Calculate moving average
    const avg = historicalData?.reduce((sum, val) => sum + val, 0) / (historicalData?.length || 1);
    const trend = historicalData?.length > 1 ? (historicalData?.[historicalData?.length - 1] - historicalData?.[0]) / historicalData?.length : 0;
    
    // Generate forecast for next 3 months
    const forecast = [];
    for (let i = 1; i <= 3; i++) {
      const predicted = Math.max(0, Math.round(avg + (trend * i)));
      const confidence = Math.max(60, 95 - (i * 10)); // Confidence decreases over time
      forecast?.push({
        month: `Mois +${i}`,
        predicted,
        confidence,
        lower: Math.round(predicted * 0.8),
        upper: Math.round(predicted * 1.2)
      });
    }
    
    return forecast;
  };

  const analyzeGeographicPerformance = (orders, deposits) => {
    const locationMap = new Map();
    
    orders?.forEach(order => {
      const location = order?.customer_city || 'Non spécifié';
      if (!locationMap?.has(location)) {
        locationMap?.set(location, {
          location,
          orders: 0,
          deposits: 0,
          revenue: 0,
          avgDeliveryTime: 0,
          stockVelocity: 0
        });
      }
      
      const loc = locationMap?.get(location);
      loc.orders += 1;
      loc.revenue += order?.total_price || 0;
    });
    
    deposits?.forEach(deposit => {
      const location = deposit?.customer_city || 'Non spécifié';
      if (locationMap?.has(location)) {
        const loc = locationMap?.get(location);
        loc.deposits += 1;
      }
    });
    
    // Calculate stock velocity (orders per day)
    const daysInRange = getDaysInRange(timeRange);
    locationMap?.forEach(loc => {
      loc.stockVelocity = loc?.orders / daysInRange;
      loc.capacityUtilization = (loc?.deposits / (loc?.orders || 1)) * 100;
    });
    
    return Array.from(locationMap?.values())?.sort((a, b) => b?.revenue - a?.revenue);
  };

  const generatePredictiveAlerts = (analytics, forecast) => {
    const alerts = [];
    
    // Low days of supply alert
    if (analytics?.daysOfSupply?.noir_270l < 14) {
      alerts?.push({
        type: 'warning',
        severity: 'high',
        title: 'Stock Noir 270L critique',
        message: `Seulement ${Math.round(analytics?.daysOfSupply?.noir_270l)} jours de stock restants`,
        action: 'Réapprovisionner immédiatement'
      });
    }
    
    if (analytics?.daysOfSupply?.bleu_220l < 14) {
      alerts?.push({
        type: 'warning',
        severity: 'high',
        title: 'Stock Bleu 220L critique',
        message: `Seulement ${Math.round(analytics?.daysOfSupply?.bleu_220l)} jours de stock restants`,
        action: 'Réapprovisionner immédiatement'
      });
    }
    
    // Demand spike prediction
    if (forecast?.length > 0 && forecast?.[0]?.predicted > analytics?.monthlyConsumption?.noir_270l * 1.5) {
      alerts?.push({
        type: 'info',
        severity: 'medium',
        title: 'Augmentation de la demande prévue',
        message: `Demande prévue: +${Math.round(((forecast?.[0]?.predicted / analytics?.monthlyConsumption?.noir_270l) - 1) * 100)}%`,
        action: 'Préparer stock supplémentaire'
      });
    }
    
    // Low collection rate
    if (analytics?.collectionRate < 70) {
      alerts?.push({
        type: 'warning',
        severity: 'medium',
        title: 'Taux de collecte faible',
        message: `Seulement ${Math.round(analytics?.collectionRate)}% des barriques collectées`,
        action: 'Optimiser planning collectes'
      });
    }
    
    return alerts;
  };

  const getFilterDate = (range) => {
    const now = new Date();
    switch(range) {
      case '7days': return new Date(now?.setDate(now?.getDate() - 7));
      case '30days': return new Date(now?.setDate(now?.getDate() - 30));
      case '90days': return new Date(now?.setDate(now?.getDate() - 90));
      case '1year': return new Date(now?.setFullYear(now?.getFullYear() - 1));
      default: return new Date(now?.setDate(now?.getDate() - 30));
    }
  };

  const getDaysInRange = (range) => {
    switch(range) {
      case '7days': return 7;
      case '30days': return 30;
      case '90days': return 90;
      case '1year': return 365;
      default: return 30;
    }
  };

  const getMonthsInRange = (range) => {
    const months = [];
    const now = new Date();
    const count = range === '1year' ? 12 : range === '90days' ? 3 : 1;
    
    for (let i = count - 1; i >= 0; i--) {
      const date = new Date(now?.getFullYear(), now?.getMonth() - i, 1);
      months?.push({
        month: date?.getMonth(),
        year: date?.getFullYear(),
        label: date?.toLocaleDateString('fr-FR', { month: 'short', year: 'numeric' })
      });
    }
    
    return months;
  };

  const handleExportReport = () => {
    // Generate CSV report
    const csvData = [
      ['Rapport Analytics Barriques', new Date()?.toLocaleDateString('fr-FR')],
      [''],
      ['KPI', 'Valeur'],
      ['Valeur Stock Total', `€${analyticsData?.totalStockValue?.toFixed(2)}`],
      ['Consommation Mensuelle Noir 270L', `${analyticsData?.monthlyConsumption?.noir_270l?.toFixed(1)} unités`],
      ['Consommation Mensuelle Bleu 220L', `${analyticsData?.monthlyConsumption?.bleu_220l?.toFixed(1)} unités`],
      ['Jours de Stock Noir 270L', `${analyticsData?.daysOfSupply?.noir_270l?.toFixed(1)} jours`],
      ['Jours de Stock Bleu 220L', `${analyticsData?.daysOfSupply?.bleu_220l?.toFixed(1)} jours`],
      ['Taux de Livraison', `${analyticsData?.deliveryRate?.toFixed(1)}%`],
      ['Taux de Collecte', `${analyticsData?.collectionRate?.toFixed(1)}%`]
    ];
    
    const csv = csvData?.map(row => row?.join(','))?.join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window?.URL?.createObjectURL(blob);
    const a = document?.createElement('a');
    a.href = url;
    a.download = `analytics-barriques-${new Date()?.toISOString()?.split('T')?.[0]}.csv`;
    a?.click();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AdminNavigation />
        <div className="flex items-center justify-center h-screen">
          <div className="text-center">
            <RefreshCw className="w-12 h-12 text-green-600 animate-spin mx-auto mb-4" />
            <p className="text-gray-600">Chargement des analyses...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <AdminNavigation />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                <BarChart3 className="w-8 h-8 text-green-600" />
                Centre Analytics Inventaire Barriques
              </h1>
              <p className="text-gray-600 mt-2">
                Business intelligence et analyses prédictives pour optimisation stratégique
              </p>
            </div>
            
            <div className="flex items-center gap-3">
              <button
                onClick={() => loadAnalyticsData(true)}
                disabled={refreshing}
                className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                Actualiser
              </button>
              
              <button
                onClick={handleExportReport}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                <Download className="w-4 h-4" />
                Exporter
              </button>
            </div>
          </div>
          
          {/* Filters */}
          <div className="mt-6 flex flex-wrap gap-4">
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-gray-500" />
              <select
                value={timeRange}
                onChange={(e) => setTimeRange(e?.target?.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              >
                <option value="7days">7 derniers jours</option>
                <option value="30days">30 derniers jours</option>
                <option value="90days">90 derniers jours</option>
                <option value="1year">1 an</option>
              </select>
            </div>
            
            <div className="flex items-center gap-2">
              <Package className="w-4 h-4 text-gray-500" />
              <select
                value={barrelTypeFilter}
                onChange={(e) => setBarrelTypeFilter(e?.target?.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              >
                <option value="all">Tous types</option>
                <option value="noir_270l">Barrique Noire 270L</option>
                <option value="bleu_220l">Barrique Bleue 220L</option>
              </select>
            </div>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold text-red-900">Erreur</h3>
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          </div>
        )}

        {/* Predictive Alerts */}
        {alerts?.length > 0 && (
          <div className="mb-6 space-y-3">
            {alerts?.map((alert, index) => (
              <div
                key={index}
                className={`border rounded-lg p-4 flex items-start gap-3 ${
                  alert?.severity === 'high' ? 'bg-red-50 border-red-200' :
                  alert?.severity === 'medium' ? 'bg-yellow-50 border-yellow-200' : 'bg-blue-50 border-blue-200'
                }`}
              >
                <AlertTriangle className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
                  alert?.severity === 'high' ? 'text-red-600' :
                  alert?.severity === 'medium' ? 'text-yellow-600' : 'text-blue-600'
                }`} />
                <div className="flex-1">
                  <h3 className={`font-semibold ${
                    alert?.severity === 'high' ? 'text-red-900' :
                    alert?.severity === 'medium' ? 'text-yellow-900' : 'text-blue-900'
                  }`}>{alert?.title}</h3>
                  <p className={`text-sm mt-1 ${
                    alert?.severity === 'high' ? 'text-red-700' :
                    alert?.severity === 'medium' ? 'text-yellow-700' : 'text-blue-700'
                  }`}>{alert?.message}</p>
                  <p className={`text-xs mt-2 font-medium ${
                    alert?.severity === 'high' ? 'text-red-800' :
                    alert?.severity === 'medium' ? 'text-yellow-800' : 'text-blue-800'
                  }`}>→ {alert?.action}</p>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* KPI Cards */}
        {analyticsData && (
          <PerformanceKPICards data={analyticsData} statistics={statistics} />
        )}

        {/* Charts Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Inventory Turnover Chart */}
          <InventoryTurnoverChart data={turnoverData} />
          
          {/* Demand Forecasting Chart */}
          <DemandForecastingChart data={demandForecast} />
        </div>

        {/* Geographic Analysis */}
        <GeographicAnalysisPanel data={geographicAnalysis} />

        {/* Strategic Insights */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-green-600" />
            Recommandations Stratégiques
          </h2>
          
          <div className="space-y-4">
            {analyticsData?.daysOfSupply?.noir_270l < 30 && (
              <div className="flex items-start gap-3 p-4 bg-blue-50 rounded-lg">
                <div className="w-2 h-2 bg-blue-600 rounded-full mt-2"></div>
                <div>
                  <h3 className="font-semibold text-blue-900">Optimiser stock Barrique Noire 270L</h3>
                  <p className="text-sm text-blue-700 mt-1">
                    Stock actuel couvre {Math.round(analyticsData?.daysOfSupply?.noir_270l)} jours. 
                    Recommandation: Commander {Math.round(analyticsData?.monthlyConsumption?.noir_270l * 2)} unités pour 60 jours de couverture.
                  </p>
                </div>
              </div>
            )}
            
            {analyticsData?.collectionRate < 80 && (
              <div className="flex items-start gap-3 p-4 bg-yellow-50 rounded-lg">
                <div className="w-2 h-2 bg-yellow-600 rounded-full mt-2"></div>
                <div>
                  <h3 className="font-semibold text-yellow-900">Améliorer taux de collecte</h3>
                  <p className="text-sm text-yellow-700 mt-1">
                    Taux actuel: {Math.round(analyticsData?.collectionRate)}%. 
                    Recommandation: Augmenter fréquence des collectes et améliorer communication avec clients.
                  </p>
                </div>
              </div>
            )}
            
            {geographicAnalysis?.length > 0 && geographicAnalysis?.[0]?.stockVelocity > 1 && (
              <div className="flex items-start gap-3 p-4 bg-green-50 rounded-lg">
                <div className="w-2 h-2 bg-green-600 rounded-full mt-2"></div>
                <div>
                  <h3 className="font-semibold text-green-900">Zone à forte demande identifiée</h3>
                  <p className="text-sm text-green-700 mt-1">
                    {geographicAnalysis?.[0]?.location} génère {geographicAnalysis?.[0]?.orders} commandes. 
                    Recommandation: Établir dépôt local pour réduire délais et coûts de transport.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}